import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-multi-select-filter',
  templateUrl: './multi-select-filter.component.html',
  styleUrls: ['./multi-select-filter.component.scss']
})
export class MultiSelectFilterComponent implements OnInit, OnDestroy {
  @Input() options: any[] = [];
  @Input() selectedValues: any[] = [];
  @Input() placeholder: string = 'Select values...';
  @Input() searchable: boolean = true;
  @Input() selectAllLabel: string = 'Select All';
  @Input() deselectAllLabel: string = 'Deselect All';

  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('dropdownMenu', { static: false }) dropdownMenu!: ElementRef;
  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef;

  filteredOptions: any[] = [];
  searchTerm: string = '';
  isOpen: boolean = false;
  private searchSubject = new Subject<string>();
  private searchSubscription: any;

  ngOnInit(): void {
    this.filteredOptions = [...this.options];
    
    if (this.searchable) {
      this.searchSubscription = this.searchSubject
        .pipe(
          debounceTime(300),
          distinctUntilChanged()
        )
        .subscribe(term => {
          this.filterOptions(term);
        });
    }
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen && this.searchable && this.searchInput) {
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      }, 100);
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.searchTerm = '';
    this.filteredOptions = [...this.options];
    this.closed.emit();
  }

  isSelected(value: any): boolean {
    return this.selectedValues.includes(value);
  }

  toggleSelection(value: any): void {
    const index = this.selectedValues.indexOf(value);
    if (index > -1) {
      this.selectedValues.splice(index, 1);
    } else {
      this.selectedValues.push(value);
    }
    this.selectionChange.emit([...this.selectedValues]);
  }

  selectAll(): void {
    this.selectedValues = [...this.filteredOptions];
    this.selectionChange.emit([...this.selectedValues]);
  }

  deselectAll(): void {
    this.selectedValues = [];
    this.selectionChange.emit([...this.selectedValues]);
  }

  removeTag(value: any, event: Event): void {
    event.stopPropagation();
    const index = this.selectedValues.indexOf(value);
    if (index > -1) {
      this.selectedValues.splice(index, 1);
      this.selectionChange.emit([...this.selectedValues]);
    }
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    if (this.searchable) {
      this.searchSubject.next(term);
    }
  }

  private filterOptions(term: string): void {
    if (!term || term.trim() === '') {
      this.filteredOptions = [...this.options];
    } else {
      const searchLower = term.toLowerCase();
      this.filteredOptions = this.options.filter(option => {
        const optionStr = String(option).toLowerCase();
        return optionStr.includes(searchLower);
      });
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (this.isOpen && this.dropdownMenu && !this.dropdownMenu.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  @HostListener('keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen) {
      this.closeDropdown();
    }
  }
}

